import { useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui";
import { type SystemInfo, type useSyncDashboard, resetCache } from "./api";
import { nomeLane, quandoBreve } from "./automatismi";
import { jobStatusLabel } from "./format";
import { Pillola, TASTO, type Tono } from "./parti";

// La parte tecnica di Impostazioni: coda dei lavori, sistema, pulizia.
// Estratta dalla pagina nel ridisegno del 28/10/2026.

function tonoJob(status: string): Tono {
  if (status === "done") return "ok";
  if (status === "failed" || status === "dead_letter") return "errore";
  if (status === "running" || status === "claimed") return "info";
  if (status === "canceled") return "spento";
  return "attenzione";
}

const NON_RIPROVABILI = ["pending", "claimed", "running"];
const NON_ANNULLABILI = ["claimed", "running", "done", "failed", "dead_letter", "canceled"];

/** Gli ultimi 20 lavori del worker: tabella sul computer, schede sul telefono. */
export function CodaLavori({
  jobs,
  error,
  isRefreshing,
  pendingIds,
  onRefresh,
  onRetry,
  onCancel,
}: {
  jobs: ReturnType<typeof useSyncDashboard>["jobs"];
  error: string | null;
  isRefreshing: boolean;
  pendingIds: Set<number>;
  onRefresh: () => void;
  onRetry: (id: number) => void;
  onCancel: (id: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3 px-5 py-3 max-md:px-4">
        <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">
          Ultimi lavori
          <span className="ml-2 font-semibold tabular-nums text-ink-3">{jobs.length}</span>
        </h3>
        <button type="button" onClick={onRefresh} className={TASTO}>
          {isRefreshing ? "Aggiorno…" : "Aggiorna"}
        </button>
      </div>
      <ErrorBanner message={error} className="mx-5 mb-3" />
      {jobs.length === 0 ? (
        <p className="border-t border-line px-5 py-5 text-[13px] text-ink">
          Nessun lavoro registrato.
        </p>
      ) : (
        <ul className="border-t border-line">
          {jobs.map((job) => {
            const motivo = job.error ? prettifyApiError(job.error) : null;
            const busy = pendingIds.has(job.id);
            return (
              <li
                key={job.id}
                className="grid grid-cols-[64px_minmax(0,1.3fr)_140px_110px_minmax(0,1.6fr)_auto] items-center gap-x-4 border-t border-line px-5 py-2.5 text-[12.5px] first:border-t-0 max-md:grid-cols-[minmax(0,1fr)_auto] max-md:gap-y-1.5 max-md:px-4 max-md:py-3 max-md:text-[14px]"
              >
                <span className="tabular-nums text-ink-2 max-md:hidden">#{job.id}</span>
                <span className="min-w-0 truncate font-bold text-ink">{nomeLane(job.module)}</span>
                <span className="max-md:col-start-2 max-md:row-start-1">
                  <Pillola tono={tonoJob(job.status)}>{jobStatusLabel(job.status)}</Pillola>
                </span>
                <span className="tabular-nums text-ink-2 max-md:text-ink">
                  {quandoBreve(job.finished_at ?? job.scheduled_at)}
                  {job.attempts > 1 && ` · tentativo ${job.attempts}/${job.max_attempts}`}
                </span>
                <span
                  className="min-w-0 truncate text-ink-2 max-md:col-span-2 max-md:whitespace-normal max-md:text-ink"
                  title={motivo ?? undefined}
                >
                  {motivo ?? ""}
                </span>
                <span className="flex justify-end gap-2 max-md:col-span-2 max-md:justify-start">
                  {!NON_RIPROVABILI.includes(job.status) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onRetry(job.id)}
                      className={TASTO}
                    >
                      Riprova
                    </button>
                  )}
                  {!NON_ANNULLABILI.includes(job.status) && (
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => onCancel(job.id)}
                      className={TASTO}
                    >
                      Annulla
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const NON_LETTO = { tono: "spento" as Tono, nome: "Non letto" };

const MIGRAZIONE: Record<string, { tono: Tono; nome: string }> = {
  ok: { tono: "ok", nome: "Allineato" },
  ahead: { tono: "attenzione", nome: "Più nuovo dell'app: ricarica dopo il rilascio" },
  error: { tono: "errore", nome: "Aggiornamento non riuscito" },
  unknown: { tono: "spento", nome: "Non letto" },
};

/** Versione e ambiente: quello che si copia in una segnalazione. */
export function SistemaInfo({ system, error }: { system: SystemInfo; error: string | null }) {
  const [copiato, setCopiato] = useState<"si" | "no" | null>(null);
  if (error) return <ErrorBanner message={error} className="m-5" />;
  const m = MIGRAZIONE[system.migration_status ?? "unknown"] ?? NON_LETTO;
  const righe: [string, string][] = [
    ["Versione", system.version || "—"],
    ["Ambiente", system.env || "—"],
    ["Server", system.platform || "—"],
    ["Python", system.python_version || "—"],
    ["Cartella dati", system.data_dir || "—"],
    ["Cartella log", system.log_dir || "—"],
  ];

  async function copia() {
    try {
      await navigator.clipboard.writeText(
        `Order Manager ${system.version} · ${system.env} · ${system.platform}`,
      );
      setCopiato("si");
    } catch {
      setCopiato("no");
    }
  }

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">Sistema</h3>
        <button type="button" onClick={() => void copia()} className={TASTO}>
          {copiato === "si"
            ? "Copiato"
            : copiato === "no"
              ? "Copia non riuscita"
              : "Copia versione"}
        </button>
      </div>
      <dl className="grid grid-cols-[130px_minmax(0,1fr)] gap-x-4 text-[13px] max-md:text-[14.5px]">
        <dt className="border-t border-line py-2 text-ink-2 max-md:text-ink">Database</dt>
        <dd className="border-t border-line py-2">
          <Pillola tono={m.tono}>{m.nome}</Pillola>
          {system.migration_detail && (
            <span className="ml-2 break-all text-[12px] text-ink-2">{system.migration_detail}</span>
          )}
        </dd>
        {righe.map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="border-t border-line py-2 text-ink-2 max-md:text-ink">{k}</dt>
            <dd className="break-all border-t border-line py-2 tabular-nums text-ink">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/** Pulizia manuale: lo stesso lavoro dell'automatismo «Pulizia storico
 *  lavori», piu' gli allegati vecchi. Con conferma in linea, niente finestra. */
export function Pulizia() {
  const [conferma, setConferma] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fatto, setFatto] = useState(false);

  async function pulisci() {
    setBusy(true);
    setError(null);
    try {
      await resetCache(30);
      setFatto(true);
      setConferma(false);
    } catch (err) {
      setError(prettifyApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-5 py-4 max-md:px-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 max-w-[62ch]">
          <h3 className="text-[15px] font-bold text-ink max-md:text-[17px]">Pulizia cache</h3>
          <p className="mt-0.5 text-[12.5px] leading-snug text-ink-2 max-md:text-[14px] max-md:text-ink">
            Toglie gli allegati salvati e lo storico dei lavori piu' vecchi di 30 giorni. Ordini,
            fatture e messaggi non si toccano.
          </p>
        </div>
        {conferma ? (
          <div className="flex gap-2">
            <button type="button" onClick={() => setConferma(false)} className={TASTO}>
              Annulla
            </button>
            <button
              type="button"
              onClick={() => void pulisci()}
              disabled={busy}
              className={`${TASTO} border-red bg-red text-white hover:border-red hover:brightness-95`}
            >
              {busy ? "Pulisco…" : "Sì, pulisci"}
            </button>
          </div>
        ) : (
          <button type="button" onClick={() => setConferma(true)} className={TASTO}>
            Pulisci cache
          </button>
        )}
      </div>
      <ErrorBanner message={error} className="mt-3" />
      {fatto && !error && (
        <p role="status" className="mt-3 rounded-md bg-green-soft px-3 py-2 text-[12.5px] text-ink">
          Pulizia fatta.
        </p>
      )}
    </div>
  );
}
